"use client";

import Image from "next/image";
import { WiestLogo } from "@/app/components/logos";
import { LinkAnchor } from "@/app/components/ui/link-anchor";
import { Button } from "@/app/components/ui/button";
import { BookmarkIcon } from "lucide-react";
import { useBookmark } from "@/app/hooks/use-bookmark";
import { cn } from "@/lib/utils";
import { sortLanguages } from "@/app/utils/helpers/sort-languages";
import { sortMakes } from "@/app/utils/helpers/sort-makes";
import { filterServiceMakes } from "@/app/utils/helpers/filter-makes";
import type { ContactPersonImageT } from "@/app/types/contact-persons";
import type { MakeT, MakeLogoT } from "@/app/types/makes";
import type { LocationT } from "@/app/types/locations";
import type { LanguageT } from "@/app/types/languages";

export default function TeamCard({
  name,
  title,
  phone,
  email,
  languages,
  image,
  makes,
  locations,
  isNameClickable = true,
  showServiceMakes = false,
  slug,
  id,
}: {
  name: string | null;
  title: string | null;
  phone: string | null;
  email: string | null;
  image: ContactPersonImageT | null;
  makes: (MakeT & { make_logo: MakeLogoT | null })[];
  locations: LocationT[];
  languages: (LanguageT & { image: { url: string | null } | null })[];
  isNameClickable?: boolean;
  showServiceMakes?: boolean;
  slug?: string;
  id?: string;
}) {
  const { toggleContactPersonBookmark, isContactPersonBookmarked } =
    useBookmark();
  const isBookmarked = id ? isContactPersonBookmarked(id) : false;

  const handleBookmarkToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (id) {
      toggleContactPersonBookmark(id);
    }
  };

  const visibleMakes = showServiceMakes ? makes : filterServiceMakes(makes);

  return (
    <div className="bg-background relative flex h-full flex-row rounded-lg shadow-md md:overflow-hidden">
      <Image
        className="outline-muted absolute -top-5 right-8 size-[96px] shrink-0 rounded-full object-cover object-[center_20%] outline-6 sm:right-10 sm:size-[120px] md:relative md:top-0 md:right-0 md:aspect-auto md:h-full md:w-[176px] md:rounded-none md:object-center md:outline-0"
        width={352}
        height={488}
        sizes="(max-width: 640px) 96px, (max-width: 768px) 120px, 176px"
        quality={95}
        alt={name ?? "Unbekannt"}
        src={image?.url || "/wiest_ansprechpartner_image_fallback.webp"}
      />
      <div className="flex min-w-0 flex-1 flex-col p-5 sm:p-6">
        <div className="md:absolute md:top-5 md:right-4">
          {visibleMakes && visibleMakes.length > 0 ? (
            <div className="flex flex-row gap-1.5">
              {visibleMakes
                .slice()
                .sort(sortMakes)
                .map((make, idx) =>
                  make.make_logo?.url ? (
                    <div
                      key={make.make_logo.url + idx}
                      className="flex items-center justify-center"
                    >
                      <Image
                        src={make.make_logo.url}
                        alt={make.name}
                        width={32}
                        height={32}
                        className={`inline-flex max-h-[1.7rem] min-h-[.7rem] w-full max-w-[100%] min-w-[1.7rem] items-start object-contain ${!make.make_logo?.url && "shadow"}`}
                      />
                    </div>
                  ) : null
                )}
            </div>
          ) : (
            <WiestLogo className="w-24" />
          )}
        </div>
        <h2 className="heading-medium pt-4 pb-2 font-bold! sm:pt-5">
          {isNameClickable && slug ? (
            <LinkAnchor href={`/ansprechpartner/${slug}`}>
              {name ?? "Unbekannt"}
            </LinkAnchor>
          ) : (
            (name ?? "Unbekannt")
          )}
        </h2>
        <div className="font-semibold">{title ?? ""}</div>
        <div>
          {locations && locations.length > 0
            ? locations.map((l) => l.city).join(", ")
            : ""}
        </div>
        <div className="pt-4">
          Tel: <LinkAnchor href={`tel:${phone}`}>{phone ?? "-"}</LinkAnchor>
        </div>
        <div>
          E-Mail:{" "}
          <LinkAnchor href={`mailto:${email}`}>{email ?? "-"}</LinkAnchor>
        </div>

        {/* Sprachen-Icons */}
        <div className="flex flex-row gap-2 pt-4">
          {languages && languages.length > 0
            ? languages
                .slice()
                .sort(sortLanguages)
                .map((language, idx) =>
                  language.image?.url ? (
                    <Image
                      key={language.image.url + idx}
                      src={language.image.url}
                      alt={language.name}
                      title={language.name}
                      width={26}
                      height={16}
                      className="h-[16px] w-[26px] overflow-hidden rounded-xs object-cover shadow-lg"
                    />
                  ) : null
                )
            : null}
        </div>
      </div>
      {/* Bookmark Button */}
      {id && (
        <div className="absolute right-4 bottom-5">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={handleBookmarkToggle}
            className={cn(
              isBookmarked
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            )}
            aria-label={
              isBookmarked
                ? "Ansprechpartner entfernen"
                : "Ansprechpartner zu Favoriten"
            }
          >
            <BookmarkIcon
              size={16}
              fill={isBookmarked ? "currentColor" : "none"}
              strokeWidth={1.5}
            />
            <span className="text-xs font-normal">zu Favoriten</span>
          </Button>
        </div>
      )}
    </div>
  );
}
